"use client";

import { formatWorkspaceDate } from "@/lib/display-preferences";

import { useMemo, useState } from "react";
import type { ConnectorSnapshot } from "@/lib/connectors/types";
import {
  projectionTerminalCount,
  type ProjectionSet,
  type TradeCandidate,
} from "@/lib/projections";
import { systems } from "@/lib/systems";

type ProjectedTimelineProps = {
  connectors: ConnectorSnapshot[];
  projectionSets: ProjectionSet[];
  anchorTime: string | null;
  loading: boolean;
  onOpenSystems: () => void;
  onOpenSimulations: () => void;
};

function probabilityLabel(basisPoints: number) {
  return `${(basisPoints / 100).toFixed(2)}%`;
}

function anchorLabel(value: string | null) {
  if (!value) return "Awaiting source refresh";
  return formatWorkspaceDate(new Date(value), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function futureLabel(anchorTime: string | null, offsetDays: number) {
  if (!anchorTime) return offsetDays === 0 ? "Present" : `+${offsetDays} days`;
  const future = new Date(anchorTime);
  future.setUTCDate(future.getUTCDate() + offsetDays);
  return formatWorkspaceDate(future, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function metricLabel(value: number | null) {
  if (value === null) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function TradeRow({ candidate }: { candidate: TradeCandidate }) {
  return (
    <div className="trade-candidate">
      <header>
        <div>
          <span>Trade candidate</span>
          <strong>{candidate.action} · {candidate.instrument}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{candidate.status}</strong>
        </div>
        <div>
          <span>Maximum allocation</span>
          <strong>{candidate.maximumAllocationPct}%</strong>
        </div>
      </header>
      <div className="trade-metrics">
        <div><span>Expected return</span><strong>{metricLabel(candidate.expectedReturnPct)}</strong></div>
        <div><span>Loss probability</span><strong>{metricLabel(candidate.probabilityOfLossPct)}</strong></div>
        <div><span>5% expected shortfall</span><strong>{metricLabel(candidate.expectedShortfallPct)}</strong></div>
        <div><span>Market edge</span><strong>{candidate.marketEdge}</strong></div>
      </div>
      <div className="trade-thesis">
        <p>{candidate.thesis}</p>
        <dl>
          <div><dt>Eligibility gate</dt><dd>{candidate.entryCondition}</dd></div>
          <div><dt>Invalidation</dt><dd>{candidate.invalidation}</dd></div>
        </dl>
      </div>
    </div>
  );
}

export default function ProjectedTimeline({
  connectors,
  projectionSets,
  anchorTime,
  loading,
  onOpenSystems,
  onOpenSimulations,
}: ProjectedTimelineProps) {
  const [selectedSet, setSelectedSet] = useState<string | null>(null);
  const [expandedTrajectory, setExpandedTrajectory] = useState<string | null>(null);
  const connectorById = useMemo(
    () => new Map(connectors.map((connector) => [connector.id, connector])),
    [connectors],
  );
  const terminalCount = projectionTerminalCount(projectionSets);
  const totalParticles = projectionSets.reduce((sum, set) => sum + set.particleCount, 0);

  return (
    <div className="projected-timeline forecast-workspace" aria-busy={loading} aria-live="polite">
      <label className="projection-selector"><span>Scenario model</span><select value={selectedSet ?? projectionSets[0]?.id ?? ""} onChange={event => { setSelectedSet(event.target.value); setExpandedTrajectory(null); }}>{projectionSets.map(set => <option key={set.id} value={set.id}>{set.title}</option>)}</select></label>
      <details className="forecast-contract" aria-label="Projection model contract"><summary>Model assumptions</summary>
        <div>
          <span>Present root</span>
          <strong>{anchorLabel(anchorTime)}</strong>
        </div>
        <div>
          <span>Forecast universe</span>
          <strong>{projectionSets.length} sets · {terminalCount} represented regimes · {totalParticles.toLocaleString()} paths</strong>
        </div>
        <div>
          <span>Normalization</span>
          <strong>Mutually exclusive siblings sum to 100% inside each set</strong>
        </div>
        <div>
          <span>Uncertainty</span>
          <strong>Probability, confidence, and calibration remain separate</strong>
        </div>
      </details>

      <div className="forecast-register">
        {projectionSets.filter(set => set.id === (selectedSet ?? projectionSets[0]?.id)).map((set, setIndex) => {
          const totalProbability = set.trajectories.reduce(
            (sum, trajectory) => sum + trajectory.probabilityBps,
            0,
          );
          const linkedSystems = set.systemIds.map((systemId) =>
            systems.find((system) => system.id === systemId)?.name ?? systemId);

          return (
            <details
              className="forecast-set"
              id={`projection-${set.id}`}
              key={set.id}
              open
            >
              <summary>
                <span className="forecast-index">{String(setIndex + 1).padStart(2, "0")}</span>
                <div className="forecast-name">
                  <small>{set.family}</small>
                  <strong>{set.title}</strong>
                </div>
                <span className="forecast-target">{set.target}</span>
                <span className="forecast-horizon">{set.horizonDays}d</span>
                <div className="forecast-total">
                  <strong>{probabilityLabel(totalProbability)}</strong>
                  <span>terminal mass</span>
                </div>
              </summary>

              <div className="forecast-detail">
                <div className="forecast-branch-map" aria-label="Possible paths from current conditions">
                  <span className="forecast-map-root">Current conditions</span>
                  <svg className="forecast-map-lines" viewBox="0 0 400 300" preserveAspectRatio="none" aria-hidden="true">
                    {set.trajectories.map((trajectory, index) => {
                      const y = (index + 0.5) * 300 / set.trajectories.length;
                      return <path key={trajectory.id} d={`M 0 150 C 180 150, 220 ${y}, 400 ${y}`} />;
                    })}
                  </svg>
                  <div className="forecast-map-outcomes">{set.trajectories.map(trajectory => <button type="button" key={trajectory.id} aria-controls={`projection-${trajectory.id}`} aria-expanded={expandedTrajectory === trajectory.id} onClick={() => setExpandedTrajectory(current => current === trajectory.id ? null : trajectory.id)}><strong>{trajectory.title}</strong><span>{probabilityLabel(trajectory.probabilityBps)}</span></button>)}</div>
                </div>
                <p className="forecast-map-total">Probability within this scenario set · {probabilityLabel(totalProbability)} total</p>
                <div className="forecast-meta">
                  <div><span>Simulation</span><strong>{set.simulationModelId} · v{set.modelVersion}</strong></div>
                  <div><span>Model confidence</span><strong>{set.modelConfidence}/100</strong></div>
                  <div><span>Calibration</span><strong>{set.calibration.status} · {set.calibration.outOfSampleForecasts} forecasts</strong></div>
                  <div><span>Partition rule</span><strong>{set.partitionRule}</strong></div>
                </div>

                <div className="forecast-actions">
                  <button type="button" onClick={onOpenSimulations}>Open Simulation</button>
                  <button type="button" onClick={onOpenSystems}>Open Systems</button>
                </div>

                <details className="forecast-sequence-disclosure" open={expandedTrajectory ? true : undefined}><summary>Conditions and projected sequence</summary>
                <div className="forecast-branches">
                  {set.trajectories.map((trajectory, trajectoryIndex) => {
                    const completeSequence = [...set.sharedCheckpoints, ...trajectory.checkpoints]
                      .sort((left, right) => left.offsetDays - right.offsetDays);

                    return (
                      <details
                        className="forecast-trajectory"
                        id={`projection-${trajectory.id}`}
                        key={trajectory.id}
                        open={expandedTrajectory === trajectory.id || undefined}
                      >
                        <summary>
                          <div className="trajectory-probability">
                            <strong>{probabilityLabel(trajectory.probabilityBps)}</strong>
                            <i aria-hidden="true"><b style={{ width: `${trajectory.probabilityBps / 100}%` }} /></i>
                          </div>
                          <span className="trajectory-number">{String(trajectoryIndex + 1).padStart(2, "0")}</span>
                          <div className="trajectory-label">
                            <strong>{trajectory.title}</strong>
                            <span>{trajectory.summary}</span>
                          </div>
                          <ol aria-label={`${trajectory.title} sequence preview`}>
                            {completeSequence.map((node) => <li key={`${trajectory.id}-${node.offsetDays}`}>{node.title}</li>)}
                          </ol>
                          <span className="trajectory-samples">{trajectory.sampleCount.toLocaleString()} paths</span>
                        </summary>

                        <div className="forecast-trajectory-detail">
                          <ol className="forecast-sequence" aria-label={`${trajectory.title} unified projected sequence`}>
                            {completeSequence.map((node, nodeIndex) => (
                              <li key={`${trajectory.id}-${node.offsetDays}-${node.title}`}>
                                <div className="forecast-node-date">
                                  <time>{futureLabel(anchorTime, node.offsetDays)}</time>
                                  <span>{node.offsetDays === 0 ? "t₀" : `+${node.offsetDays}d`}</span>
                                </div>
                                <div className="forecast-node-marker" aria-hidden="true">
                                  <span>{String(nodeIndex + 1).padStart(2, "0")}</span>
                                </div>
                                <div className="forecast-node-content">
                                  <span>{node.kind}</span>
                                  <h3>{node.title}</h3>
                                  <p>{node.detail}</p>
                                  <dl>
                                    <div><dt>Mechanism</dt><dd>{node.mechanism}</dd></div>
                                    <div><dt>State</dt><dd>{node.stateVariables.join(" · ")}</dd></div>
                                  </dl>
                                </div>
                              </li>
                            ))}
                          </ol>

                          <div className="forecast-conditions">
                            <section>
                              <span>Supports classification</span>
                              <ul>{trajectory.supportingConditions.map((condition) => <li key={condition}>{condition}</li>)}</ul>
                            </section>
                            <section>
                              <span>Invalidates classification</span>
                              <ul>{trajectory.invalidatingConditions.map((condition) => <li key={condition}>{condition}</li>)}</ul>
                            </section>
                          </div>
                        </div>
                      </details>
                    );
                  })}
                </div>
                </details>

                {set.tradeCandidates.map((candidate) => (
                  <TradeRow candidate={candidate} key={candidate.id} />
                ))}

                <details className="forecast-evidence">
                  <summary>Inputs, systems, and known gaps</summary>
                  <div>
                    <section>
                      <span>Mediating systems</span>
                      <p>{linkedSystems.join(" · ")}</p>
                    </section>
                    <section>
                      <span>Required sources</span>
                      <ul>
                        {set.requiredSourceIds.map((sourceId) => {
                          const connector = connectorById.get(sourceId);
                          return (
                            <li key={sourceId}>
                              <strong>{connector?.name ?? sourceId}</strong>
                              <small>{connector?.status.replaceAll("_", " ") ?? "not loaded"}</small>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                    <section>
                      <span>Known gaps</span>
                      <ul>{set.sourceGaps.map((gap) => <li key={gap}>{gap}</li>)}</ul>
                    </section>
                  </div>
                </details>
              </div>
            </details>
          );
        })}
      </div>

      <p className="projection-boundary">
        Forecast probabilities are normalized only within their declared outcome sets. Model confidence is not probability. Trade candidates are simulation outputs with zero allocation, no order authority, and no claim of calibrated edge.
      </p>
    </div>
  );
}
